import axios, { AxiosResponse } from "axios";

import {
  AgriLightRequirementType,
  IAgriLightRecommendationsResponse,
  ICurrentDayPlanDto,
  IGenerateShadingPlanRequest,
  IShadingParseErrorResponse,
} from "./AgriModels";
import APP_CONFIG from "../configuration/AppConfig";

export async function uploadNewPlanAsync(siteId: string, fileData: Uint8Array): Promise<AxiosResponse> {
  const url = `${APP_CONFIG.serviceUrls.dualUsageUrlPrefix}/ShadingPlan/${siteId}`;
  const params = {
    file: fileData,
  };
  const headers = { headers: { "Content-Type": "multipart/form-data" } };
  try {
    return await axios.post(url, params, headers);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      const data = error.response.data as IShadingParseErrorResponse;
      if (data?.Message) {
        throw new Error(data.Message, { cause: error });
      }
    }
    throw error;
  }
}

export async function deleteAgriPlanAsync(siteId: string): Promise<boolean> {
  const url = `${APP_CONFIG.serviceUrls.dualUsageUrlPrefix}/ShadingPlan/${siteId}`;
  const res = await axios.delete<void>(url);
  return res.status == 200;
}

export async function getDailyPlanAsync(
  siteId: string,
  signal?: AbortSignal
): Promise<ICurrentDayPlanDto> {
  const url = `${APP_CONFIG.serviceUrls.dualUsageUrlPrefix}/ShadingPlan/CurrentDay/${siteId}`;
  const res = await axios.get<ICurrentDayPlanDto>(url, { signal });
  return res.data;
}

export async function uploadTrackerGroupsAsync(
  siteId: string,
  fileData: Uint8Array
): Promise<AxiosResponse> {
  const url = `${APP_CONFIG.serviceUrls.dualUsageUrlPrefix}/TrackerGroups/${siteId}`;
  const params = { file: fileData };
  const headers = { headers: { "Content-Type": "multipart/form-data" } };
  try {
    return await axios.post(url, params, headers);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      const message = error.response.data;
      if (typeof message === "string") {
        throw new Error(message, { cause: error });
      }
    }
    throw error;
  }
}

export async function getTrackerGroupsTemplateAsync(siteId: string): Promise<string> {
  const url = `${APP_CONFIG.serviceUrls.dualUsageUrlPrefix}/TrackerGroups/Template/${siteId}`;
  const res = await axios.get<string>(url);
  return res.data;
}

export async function getTrackerGroupsAsync(siteId: string): Promise<string> {
  const url = `${APP_CONFIG.serviceUrls.dualUsageUrlPrefix}/TrackerGroups/${siteId}`;
  const res = await axios.get<string>(url);
  return res.data;
}

export async function getTrackerGroupsExistAsync(siteId: string): Promise<boolean> {
  const url = `${APP_CONFIG.serviceUrls.dualUsageUrlPrefix}/TrackerGroups/Exists/${siteId}`;
  const res = await axios.get<boolean>(url);
  return res.data;
}

export async function getOverallPlanAsFileAsync(siteId: string) {
  const url = `${APP_CONFIG.serviceUrls.dualUsageUrlPrefix}/ShadingPlan/OverallPlan/${siteId}`;
  const res = await axios.get<string>(url);
  return res.data;
}

export async function getAgriPlanTemplateAsync() {
  const url = `${APP_CONFIG.serviceUrls.dualUsageUrlPrefix}/ShadingPlan/GetTemplateFile`;
  const res = await axios.get<string>(url);
  return res.data;
}

export async function generateShadingPlanAsync(
  siteId: string,
  startDate: string,
  endDate: string,
  targetValue: number,
  targetType: AgriLightRequirementType
): Promise<AxiosResponse> {
  const url = `${APP_CONFIG.serviceUrls.dualUsageUrlPrefix}/ShadingPlan/Generate/${siteId}`;
  const requestBody: IGenerateShadingPlanRequest = {
    startDate,
    endDate,
    targetValue,
    targetType,
  };
  const res = await axios.post(url, requestBody);
  return res;
}

export async function getAgriLightRecommendationsAsync(
  siteId: string,
  date: string,
  windowDays: number
): Promise<IAgriLightRecommendationsResponse> {
  const url = `${APP_CONFIG.serviceUrls.dualUsageUrlPrefix}/ShadingPlan/LightRecommendations/${siteId}`;
  const res = await axios.get<IAgriLightRecommendationsResponse>(url, {
    params: {
      date,
      window_days: windowDays,
    },
  });
  return res.data;
}
